import { Check, Lock } from "lucide-react";

import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/TooltipV3";
import {
  PROJECT_STEP_STATUS_LABELS,
  STUDIO_LICENSE_LABEL,
  type ProjectStepStatus,
} from "config/navigation";
import { cn } from "utils/cn";

const dotClassName: Record<ProjectStepStatus, string> = {
  ready: "h-[7px] w-[7px] rounded-full bg-presence",
  not_started: "h-1.5 w-1.5 rounded-full border-[1.5px] border-chrome-muted-foreground/70",
  running:
    "h-[7px] w-[7px] rounded-full border-[1.5px] border-secondary border-r-transparent motion-safe:animate-spin",
  failed: "h-[7px] w-[7px] rounded-full bg-danger",
};

/** The status dot alone, for the collapsed icon. */
export const StepStatusDot = ({ status }: { status: ProjectStepStatus }) => (
  <span
    aria-hidden
    data-step-status={status}
    className={cn("inline-block shrink-0", dotClassName[status])}
  />
);

/**
 * What a step row shows next to its label. Only Running and Failed carry
 * text, since those need attention. Ready is a small green check. Not
 * started is an empty circle whose tooltip says "Not generated yet". Every
 * status keeps its name for screen readers.
 */
export const StepStatusIndicator = ({ status }: { status: ProjectStepStatus }) => {
  const label = PROJECT_STEP_STATUS_LABELS[status];

  if (status === "ready") {
    return (
      <span data-step-status={status} className="inline-flex shrink-0 items-center">
        <Check className="h-3.5 w-3.5 text-presence" strokeWidth={2.6} aria-hidden />
        <span className="sr-only">{label}</span>
      </span>
    );
  }

  if (status === "not_started") {
    return (
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <span
            data-step-status={status}
            className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center"
          >
            <span
              aria-hidden
              className="h-2 w-2 rounded-full border-[1.5px] border-chrome-muted-foreground/70"
            />
            <span className="sr-only">{label}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={6}>
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <span
      data-step-status={status}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 text-[10px] font-medium",
        status === "failed" ? "text-danger" : "text-chrome-muted-foreground"
      )}
    >
      <StepStatusDot status={status} />
      <span>{label}</span>
    </span>
  );
};

/** "Studio" lock pill on steps a Writer license does not include. */
export const StudioLockPill = ({ iconOnly = false }: { iconOnly?: boolean }) => (
  <span
    className={cn(
      "inline-flex shrink-0 items-center gap-1 rounded-full bg-chrome-muted text-[10px] font-semibold text-chrome-muted-foreground",
      iconOnly ? "p-[3px]" : "px-1.5 py-px"
    )}
  >
    <Lock className="h-[11px] w-[11px]" aria-hidden />
    {iconOnly ? <span className="sr-only">{STUDIO_LICENSE_LABEL}</span> : STUDIO_LICENSE_LABEL}
  </span>
);
